import type { TextLayer } from '../core/model/types';
import { fontShorthand, layoutText, textBox } from '../core/paint/text';

/**
 * Renders a text layer's glyphs into a 2D context.
 *
 * This is the one text rasterizer the whole app shares: the live export path
 * (`exportBlob`), merges and flattens (`rasterizeRegion`) all draw through it,
 * so a text layer's ink is identical wherever it is baked. The context must
 * already be in the layer's own pixel space — origin at the layer's top-left,
 * unrotated, unscaled; callers compose their own transforms around it
 * (`placeLayer` for exports, the centre-origin composition in
 * `rasterizeRegion`).
 *
 * The anchor follows the box the model computed (`textBox`), not the wrap
 * width: a right-aligned line with no wrap width still ends at the box's
 * right edge, as the live Textbox draws it.
 */
export function drawTextLayer(ctx: CanvasRenderingContext2D, layer: TextLayer): void {
  const layout = layoutText(layer);
  const box = textBox(layer);
  ctx.font = fontShorthand(layer);
  ctx.fillStyle = layer.color;
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = layer.align;
  const anchorX = layout.anchorX * box.width;
  layout.lines.forEach((line, index) => {
    ctx.fillText(line, anchorX, layout.firstBaseline + index * layout.lineHeight);
  });
}

/**
 * The layer's glyphs as a standalone PNG, in its own (unscaled) pixel space.
 *
 * This is the bake door: a canvas flip has to mirror a text layer's glyphs,
 * and live text cannot represent that, so the executor rasterizes the layer
 * through here and the transform machinery takes it from there — the same
 * trade Merge Down makes, at a door the user opened deliberately.
 */
export function textLayerToDataUrl(layer: TextLayer): string {
  const box = textBox(layer);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(box.width));
  canvas.height = Math.max(1, Math.round(box.height));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This browser cannot render a canvas.');
  drawTextLayer(ctx, layer);
  return canvas.toDataURL('image/png');
}
